import { useState } from 'react';
import toast from 'react-hot-toast';
import { Card, StatCard } from '../components/Card';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog } from '../components/admin/AdminActions';
import { inputClass, secondaryButton } from '../components/admin/AdminUI';
import { useAdminResource } from './directory/useAdminResource';
import { adminRequest, useAdminAction } from './directory/adminRequest';
import { DirectoryError } from './directory/parts';
import { formatDateTime, humanize } from './directory/labels';

type Channel = 'email' | 'sms';

interface OutboxRow {
  id: string;
  appId: string | null;
  templateId: string | null;
  toAddress?: string;
  toNumber?: string;
  to?: string;
  subject?: string | null;
  status: string;
  attemptCount: number;
  maxAttempts: number;
  lastError: string | null;
  createdAt: string;
}

const STATUSES: Record<Channel, string[]> = {
  email: ['pending', 'processing', 'sent', 'failed', 'skipped'],
  sms: ['pending', 'processing', 'sent', 'delivered', 'failed', 'undelivered', 'skipped'],
};

function statusTone(status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  if (status === 'sent' || status === 'delivered') return 'success';
  if (status === 'failed' || status === 'undelivered') return 'error';
  if (status === 'pending' || status === 'processing') return 'info';
  return 'neutral';
}

/**
 * The email or SMS outbox: what Central has queued and sent, with the two
 * operator actions its API already supports — retry a failed message, and run
 * the delivery worker now.
 */
export function MessageOutbox({ channel }: { channel: Channel }) {
  const [status, setStatus] = useState('');
  const [version, setVersion] = useState(0);
  const listPath = `/${channel}/outbox?limit=25${status ? `&status=${status}` : ''}&v=${version}`;
  const list = useAdminResource<{ emails?: OutboxRow[]; sms?: OutboxRow[]; total: number }>(listPath);
  const metrics = useAdminResource<Record<string, unknown>>(`/${channel}/metrics?v=${version}`);
  const [retrying, setRetrying] = useState<OutboxRow | null>(null);
  const [running, setRunning] = useState(false);
  const retryAction = useAdminAction();
  const runAction = useAdminAction();
  const rows = (channel === 'email' ? list.data?.emails : list.data?.sms) ?? [];
  const m = metrics.data ?? {};
  const num = (k: string) => (typeof m[k] === 'number' ? (m[k] as number) : 0);
  const label = channel === 'email' ? 'Email' : 'SMS';
  const configured = channel === 'email' ? m['sesConfigured'] === true : m['twilioConfigured'] === true;
  const refresh = () => setVersion((n) => n + 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label={`${label} pending`} value={num(channel === 'email' ? 'pendingEmails' : 'pendingSms').toLocaleString()} />
        <StatCard label="Sent" value={num(channel === 'email' ? 'sentEmails' : 'sentSms').toLocaleString()} status="success" />
        <StatCard
          label="Failed"
          value={num(channel === 'email' ? 'failedEmails' : 'failedSms').toLocaleString()}
          status={num(channel === 'email' ? 'failedEmails' : 'failedSms') > 0 ? 'warning' : 'neutral'}
        />
        <StatCard
          label={channel === 'email' ? 'Email provider (SES)' : 'SMS provider (Twilio)'}
          value={configured ? 'Configured' : 'Not configured'}
          status={configured ? 'success' : 'warning'}
        />
      </div>

      <Card
        title={`${label} outbox`}
        description="Newest first. Retry puts a failed message back in the queue."
        actions={
          <button type="button" className={secondaryButton} onClick={() => setRunning(true)}>
            Run {label.toLowerCase()} worker now
          </button>
        }
      >
        <div className="mb-3 flex items-center gap-2">
          <label className="text-sm text-slate-600" htmlFor={`${channel}-status`}>
            Status
          </label>
          <select
            id={`${channel}-status`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`${inputClass} max-w-[12rem]`}
          >
            <option value="">All</option>
            {STATUSES[channel].map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </select>
        </div>
        {list.error ? (
          <DirectoryError error={list.error} what={`${label} outbox`} onRetry={list.reload} />
        ) : list.loading ? (
          <p role="status" className="text-sm text-slate-500">
            Loading…
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-6 text-center text-sm text-slate-500">
            No {label.toLowerCase()} messages{status ? ` with status ${status}` : ''}.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  {['To', channel === 'email' ? 'Subject' : 'Template', 'Status', 'Attempts', 'Created', ''].map((h) => (
                    <th key={h} className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {rows.map((r) => (
                  <tr key={r.id} className="transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40">
                    <td className="border-b border-slate-100 px-4 py-3.5 text-slate-800">{r.toAddress ?? r.toNumber ?? r.to ?? '—'}</td>
                    <td className="border-b border-slate-100 px-4 py-3.5 text-slate-600">{(channel === 'email' ? r.subject : r.templateId) ?? '—'}</td>
                    <td className="border-b border-slate-100 px-4 py-3.5">
                      <StatusBadge status={statusTone(r.status)} label={humanize(r.status)} />
                      {r.lastError && <p className="mt-1 max-w-xs truncate text-xs text-red-600">{r.lastError}</p>}
                    </td>
                    <td className="border-b border-slate-100 px-4 py-3.5 text-slate-600">
                      {r.attemptCount}/{r.maxAttempts}
                    </td>
                    <td className="border-b border-slate-100 px-4 py-3.5 text-slate-600">{formatDateTime(r.createdAt)}</td>
                    <td className="border-b border-slate-100 px-4 py-3.5 text-right">
                      {r.status === 'failed' && (
                        <button type="button" className={secondaryButton} onClick={() => setRetrying(r)}>
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retrying !== null}
        title={`Retry this ${label.toLowerCase()}?`}
        body={<p>It goes back in the queue and is sent on the worker's next run.</p>}
        confirmLabel="Retry"
        busy={retryAction.busy}
        error={retryAction.error}
        onClose={() => setRetrying(null)}
        onConfirm={async () => {
          const ok = await retryAction.run(() => adminRequest('POST', `/${channel}/outbox/${retrying!.id}/retry`));
          if (ok !== undefined) {
            toast.success('Queued for retry');
            setRetrying(null);
            refresh();
          }
        }}
      />
      <ConfirmDialog
        open={running}
        title={`Run the ${label.toLowerCase()} worker now?`}
        body={
          <p>
            Sends whatever is due in the {label.toLowerCase()} queue right now instead of waiting for its schedule.
            {!configured && ' The provider is not configured here, so due messages will fail or be skipped.'}
          </p>
        }
        confirmLabel="Run now"
        busy={runAction.busy}
        error={runAction.error}
        onClose={() => setRunning(false)}
        onConfirm={async () => {
          const res = await runAction.run(() =>
            adminRequest<{ processed?: number; sent?: number; failed?: number }>('POST', `/${channel}/worker/run`),
          );
          if (res) {
            toast.success(`Worker ran: ${res.processed ?? 0} processed, ${res.sent ?? 0} sent, ${res.failed ?? 0} failed`);
            setRunning(false);
            refresh();
          }
        }}
      />
    </div>
  );
}
